// Utilidades SSOT para montar o texto de Contexto (Modal/Slack)
// Regras: texto padronizado e centralizado (SRP/SSOT) para evitar divergência entre UI e integrações.

type AnyObj = Record<string, any>

// Labels canônicos de timeframe em PT-BR
export function tfLabelPT(tfRaw?: string | null): string | null {
  const t = String(tfRaw || '').toUpperCase()
  if (!t) return null
  if (t === '5') return '5min'
  if (t === '15') return '15min'
  if (t === '30') return '30min'
  if (t === '60') return '1h'
  if (t === '240') return '4h'
  if (t === '720') return '12h'
  if (t === 'D') return 'Diário'
  if (t === 'W') return 'Semanal'
  if (t === 'M') return 'Mensal'
  return t
}

// TF imediatamente superior (usado em textos de contexto)
export function minorForMajorTF(tfRaw?: string | null): string | null {
  const tf = String(tfRaw || '').toUpperCase()
  if (!tf) return null
  // Cadeia oficial: 5 → 15 → 30 → 60 → 240 → 720 → D → W → M
  if (tf === '5') return '15'
  if (tf === '15') return '30'
  if (tf === '30') return '60'
  if (tf === '60') return '240'
  if (tf === '240') return '720'
  if (tf === '720') return 'D'
  if (tf === 'D') return 'W'
  if (tf === 'W') return 'M'
  return null
}

// Correspondências diretas para TF "sinal" a partir do TF alvo (Regra 4.3 oficial)
// Major (alvo) -> TF menor correspondente (sinal)
// M -> D, W -> 240, D -> 60, 720 -> 30, 240 -> 15, 60 -> 5
export function signalTfForTarget(tfRaw?: string | null): string | null {
  const tf = String(tfRaw || '').toUpperCase()
  if (!tf) return null
  if (tf === 'M') return 'D'
  if (tf === 'W') return '240'
  if (tf === 'D') return '60'
  if (tf === '720') return '30'
  if (tf === '240') return '15'
  if (tf === '60') return '5'
  // abaixo de 1h não tem correspondente oficial
  return null
}

function nextHigherTF(tfRaw?: string | null): string | null {
  return minorForMajorTF(tfRaw)
}

function num(v: unknown): number | null {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function fmt(v: number | null | undefined): string {
  const n = typeof v === 'number' ? v : (v == null ? null : Number(v))
  if (n == null || !Number.isFinite(n)) return '-'
  const abs = Math.abs(n)
  if (abs >= 1) return n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')
  if (abs >= 0.01) return n.toFixed(5).replace(/0+$/, '').replace(/\.$/, '')
  return n.toFixed(6).replace(/0+$/, '').replace(/\.$/, '')
}

function readGateEma12(cf: AnyObj): { pass?: boolean; ema12?: number | null; price?: number | null } {
  const gate = (cf?.gate_ema12 ?? cf?.ema12_gate ?? cf?.ema_gate ?? null) as AnyObj | null
  if (gate && typeof gate === 'object') {
    const pass = typeof gate.pass === 'boolean' ? (gate.pass as boolean) : undefined
    const ema12 = num((gate as any).ema12 ?? (gate as any).ema_12 ?? null)
    const price = num((gate as any).price ?? (gate as any).price_live ?? null)
    return { pass, ema12, price }
  }
  return {}
}

function readPrice(row: AnyObj, cf: AnyObj): number | null {
  return num(row?.price) ?? num(cf?.price_live) ?? null
}

function readFiboLevels(cf: AnyObj): { f382?: number; f50?: number; f618?: number } {
  const f: AnyObj | null = (cf?.fibo_levels ??
    cf?.fibo ??
    cf?.fibonacci ??
    cf?.fibo_alvo ??
    cf?.target_fibo ??
    null) as AnyObj | null

  const pick = (obj: AnyObj | null | undefined, ...keys: string[]): number | null => {
    for (const k of keys) {
      const v = (obj as any)?.[k]
      const n = num((v != null && typeof v === 'object') ? (v.price ?? v.value) : v)
      if (n != null) return n
    }
    return null
  }

  if (f && typeof f === 'object') {
    const f382 = pick(f, '0.382', '382', '38.2')
    const f50 = pick(f, '0.5', '50', '50.0')
    const f618 = pick(f, '0.618', '618', '61.8')
    return {
      f382: f382 ?? undefined,
      f50: f50 ?? undefined,
      f618: f618 ?? undefined,
    }
  }

  // Fallback: tentar construir com range alvo (target_high/low ou range_high/low)
  let hi = num(cf?.target_high ?? cf?.range_high)
  let lo = num(cf?.target_low ?? cf?.range_low)
  if (hi != null && lo != null && hi > lo) {
    const range = hi - lo
    return {
      f382: hi - 0.382 * range,
      f50: hi - 0.5 * range,
      f618: hi - 0.618 * range,
    }
  }

  return {}
}

function readMinorEma200(cf: AnyObj): number | null {
  const keys = [
    'ema200_menor',
    'ema_200_menor',
    'minor_ema200',
    'ema200_minor',
    'ema_200_minor',
    'ema200_signal',
    'ema_200_signal',
    'signal_ema200',
    'signal_ema_200',
  ]
  for (const k of keys) {
    const v = (cf as any)?.[k]
    const n = num((v != null && typeof v === 'object') ? (v.price ?? v.value) : v)
    if (n != null) return n
  }
  return null
}

// Export: extrai níveis de Fibo (0.382/0.5/0.618) para exibição em “Dados técnicos”
export function extractFiboLevels(row: AnyObj): { f382?: number | null; f50?: number | null; f618?: number | null } {
  const cf: AnyObj = (row?.confluence_factors ?? row?.confluence ?? {}) as AnyObj
  const { f382, f50, f618 } = readFiboLevels(cf)
  return {
    f382: f382 ?? null,
    f50: f50 ?? null,
    f618: f618 ?? null,
  }
}

// Bloco de contexto principal (título + linhas de EMAs/Fibo/EMA200)
export function buildContextParts(row: AnyObj): {
  header: string
  emasLine: string
  fiboLine?: string
  ema200Line?: string
} {
  const cf: AnyObj = (row?.confluence_factors ?? row?.confluence ?? {}) as AnyObj
  const symbol = String(row?.symbol || '').toUpperCase()
  const trendRaw = String(row?.target_trend ?? cf.trend_alvo ?? '').toLowerCase()
  const bullish = trendRaw === 'bullish' || trendRaw === 'alta' || trendRaw === 'long'
  const pivotLabel = bullish ? 'fundo ascendente' : 'topo descendente'
  const tfMandRaw = row?.target_timeframe ?? cf.target_timeframe_raw
  const tfMand = tfLabelPT(tfMandRaw) || '-'
  const tfMenor = tfLabelPT(signalTfForTarget(tfMandRaw)) || '-'

  const header = `${symbol} iniciou uma correção no tempo gráfico ${tfMand} em busca do seu ${pivotLabel}.`

  // EMA12 gate / invalidação textual
  const gate = readGateEma12(cf)
  const price = readPrice(row, cf)
  const ema12 = gate.ema12 ?? num(cf?.ema12 ?? cf?.ema_12)
  let ema12Invalid = false
  if (typeof gate.pass === 'boolean') {
    // alinhado ao gate do alvo
    ema12Invalid = !gate.pass
  } else if (price != null && ema12 != null) {
    ema12Invalid = bullish ? price < ema12 : price > ema12
  }

  let emasLine = ''
  if (ema12Invalid) {
    emasLine = bullish
      ? `Observação de EMAs: no ${tfMand}, o preço está abaixo da EMA 12 (suporte invalidado). Mantém relevância a EMA 26 do ${tfMand} em conjunto com o RSI do ${tfMenor} em sobrevenda.`
      : `Observação de EMAs: no ${tfMand}, o preço está acima da EMA 12 (resistência invalidada). Mantém relevância a EMA 26 do ${tfMand} em conjunto com o RSI do ${tfMenor} em sobrecompra.`
  } else {
    emasLine = bullish
      ? `Suportes importantes podem ser em EMA 12, EMA 26 e regiões de Fibo, juntamente com o RSI do ${tfMenor} em sobrevenda.`
      : `Resistências importantes podem ser em EMA 12, EMA 26 e regiões de Fibo, juntamente com o RSI do ${tfMenor} em sobrecompra.`
  }

  // Fibo dinâmico
  let fiboLine: string | undefined
  const { f382, f50, f618 } = readFiboLevels(cf)
  if (price != null && (f382 != null || f50 != null || f618 != null)) {
    if (bullish) {
      // escolher o primeiro nível abaixo do preço (mais próximo): 0.382, senão 0.5, senão 0.618
      const candidates: Array<{ key: string; val: number }> = []
      if (f382 != null && f382 <= price) candidates.push({ key: '0.382', val: f382 })
      if (f50 != null && f50 <= price) candidates.push({ key: '0.5', val: f50 })
      if (f618 != null && f618 <= price) candidates.push({ key: '0.618', val: f618 })
      if (candidates.length > 0) {
        // manter ordem de preferência 0.382 → 0.5 → 0.618 (todas já estão <= preço)
        const next = candidates[0]
        if (f382 != null && price < f382) {
          // 0.382 já ficou acima do preço: explicitar perda
          fiboLine = `Fibonacci (${tfMand}): a retração de 0.382 (${fmt(f382)}) já foi perdida; próximo suporte é a ${next.key} em ${fmt(next.val)}.`
        } else {
          fiboLine = `Fibonacci (${tfMand}): próximo suporte é a ${next.key} em ${fmt(next.val)}.`
        }
      } else if (f382 != null && price < f382) {
        // Nenhum nível abaixo do preço (queda muito profunda): sinalizar perda do 0.382
        fiboLine = `Fibonacci (${tfMand}): a retração de 0.382 (${fmt(f382)}) já foi perdida.`
      }
    } else {
      // Bearish: escolher a primeira resistência acima do preço (mais próxima)
      const candidates: Array<{ key: string; val: number }> = []
      if (f618 != null && f618 >= price) candidates.push({ key: '0.618', val: f618 })
      if (f50 != null && f50 >= price) candidates.push({ key: '0.5', val: f50 })
      if (f382 != null && f382 >= price) candidates.push({ key: '0.382', val: f382 })
      if (candidates.length > 0) {
        const next = candidates[0]
        fiboLine = `Fibonacci (${tfMand}): próxima resistência é a ${next.key} em ${fmt(next.val)}.`
      }
    }
  }

  // EMA200 do timeframe menor: opcional, só exibir quando fizer sentido
  let ema200Line: string | undefined
  try {
    const ema200Minor = readMinorEma200(cf)
    if (ema200Minor != null && price != null) {
      const validAsSupport = bullish && price > ema200Minor
      const validAsResistance = !bullish && price < ema200Minor
      if (validAsSupport) {
        ema200Line = `EMA 200 do ${tfMenor}, juntamente com EMAs do ${tfMand}, podem ser regiões de suporte.`
      } else if (validAsResistance) {
        ema200Line = `EMA 200 do ${tfMenor}, juntamente com EMAs do ${tfMand}, podem ser regiões de resistência.`
      }
    } else {
      // Sem dado confiável: manter frase genérica
      ema200Line = bullish
        ? `EMA 200 do ${tfMenor}, juntamente com EMAs do ${tfMand}, podem ser regiões de suporte.`
        : `EMA 200 do ${tfMenor}, juntamente com EMAs do ${tfMand}, podem ser regiões de resistência.`
    }
  } catch {
    ema200Line = bullish
      ? `EMA 200 do ${tfMenor}, juntamente com EMAs do ${tfMand}, podem ser regiões de suporte.`
      : `EMA 200 do ${tfMenor}, juntamente com EMAs do ${tfMand}, podem ser regiões de resistência.`
  }

  return { header, emasLine, fiboLine, ema200Line }
}

// Texto “Invalida o trade”
export function buildInvalidationText(row: AnyObj): string {
  const cf: AnyObj = (row?.confluence_factors ?? row?.confluence ?? {}) as AnyObj
  const stopRaw = (row as AnyObj)?.stop_price ?? cf.stop_price ?? cf.stop
  const stop = typeof stopRaw === 'number' ? stopRaw : num(stopRaw)

  if (stop != null && Number.isFinite(stop)) {
    return `Essa an\u00e1lise t\u00e9cnica \u00e9 invalidada se o pre\u00e7o atingir ${fmt(stop)}.`
  }

  const symbol = String(row?.symbol || '').toUpperCase()
  const tfMandRaw = row?.target_timeframe ?? cf.target_timeframe_raw
  const tfSuperior = tfLabelPT(nextHigherTF(tfMandRaw)) || tfLabelPT(tfMandRaw) || '-'
  const trendRaw = String(row?.target_trend ?? cf.trend_alvo ?? '').toLowerCase()
  const bullish = trendRaw === 'bullish' || trendRaw === 'alta' || trendRaw === 'long'
  const lado = bullish ? 'mínima' : 'máxima'

  return `Essa oportunidade pode ser invalidada se o ${tfSuperior} confirmar o rompimento da ${lado} do candle anterior. Esse rompimento confirmará que ${symbol} iniciou a correção no ${tfSuperior}.`
}

